import { useState } from "react";
import { TextField } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

import CustomDatePicker from "components/common/DatePicker";

import { type FormData, EXPERIENCE_LEVELS, INPUT_SX, MIN_START_DATE } from "./types";
import "./StepBasics.scss";

// ─── Experience Level Toggle ──────────────────────────────────────────────────

// eslint-disable-next-line no-unused-vars
function ExperienceToggle({ value, onChange }: { value: string; onChange: (_v: string) => void }) {
  return (
    <div className="rah-exp-toggle">
      {EXPERIENCE_LEVELS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`rah-exp-toggle__option${value === opt.value ? " rah-exp-toggle__option--active" : ""}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Step 1: Essentials ───────────────────────────────────────────────────────

function StepBasics({
  data,
  onChange,
  onBack,
  onContinue,
  editMode = false,
}: {
  data: FormData;
  // eslint-disable-next-line no-unused-vars
  onChange: (_patch: Partial<FormData>) => void;
  onBack: () => void;
  onContinue: () => void;
  editMode?: boolean;
}) {
  const [showJobPosting, setShowJobPosting] = useState(() => !!data.jobPostingUrl.trim());
  const [errors, setErrors] = useState<{ jobTitle?: string; startDate?: string; jobPostingUrl?: string }>({});

  const handleContinueClick = () => {
    const newErrors: { jobTitle?: string; startDate?: string; jobPostingUrl?: string } = {};
    if (!data.jobTitle.trim()) newErrors.jobTitle = "Role or job title is required";
    if (!data.startDate) newErrors.startDate = "Ideal start date is required";
    if (data.jobPostingUrl.trim()) {
      // Accepts: https://..., http://..., www.domain.com, domain.com
      // Requires a real domain (letters/digits/hyphens) + dot + TLD (2+ chars)
      const urlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}(\/[^\s]*)?$/;
      if (!urlRegex.test(data.jobPostingUrl.trim())) {
        newErrors.jobPostingUrl = "Please enter a valid URL (e.g. https://example.com)";
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onContinue();
  };

  const handleUploadClick = () => {
    setShowJobPosting(true);
  };

  const handleRemovePosting = () => {
    setShowJobPosting(false);
    onChange({ jobPostingUrl: "" });
  };

  return (
    <div className="rah-form-card">
      {/* Card header */}
      <div className="rah-form-card__header">
        <h2 className="rah-form-card__title">Let&apos;s start with the essentials</h2>
        <p className="rah-form-card__subtitle">Tell us about the role and we&apos;ll take it from there.</p>
      </div>

      {/* Fields */}
      <div className="rah-form-card__fields">
        {/* ── Role or job title ── */}
        <div className="rah-form-card__field-group" style={{ gap: 8 }}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="rah-job-title"
            className="rah-form-card__field-label"
            style={{ display: "block", padding: "4px 0" }}
          >
            Role or job title
          </label>
          <TextField
            inputProps={{ id: "rah-job-title" }}
            placeholder="e.g. Social Media Manager, Medical Biller"
            value={data.jobTitle}
            onChange={(e) => {
              onChange({ jobTitle: e.target.value });
              if (errors.jobTitle && e.target.value.trim()) setErrors((prev) => ({ ...prev, jobTitle: undefined }));
            }}
            variant="outlined"
            size="small"
            fullWidth
            error={!!errors.jobTitle}
            sx={INPUT_SX}
          />
          {errors.jobTitle && <p className="rah-field-error">{errors.jobTitle}</p>}

          {/* Job posting toggle */}
          {!showJobPosting ? (
            <button type="button" className="rah-form-card__job-posting-btn" onClick={handleUploadClick}>
              Already have a job posting? Add the link.
            </button>
          ) : (
            <div className="rah-form-card__field-group" style={{ gap: 4, width: "100%" }}>
              <div className="rah-form-card__posting-label-row">
                <span className="rah-form-card__field-label">Job posting link (optional)</span>
                <button type="button" className="rah-form-card__remove-btn" onClick={handleRemovePosting}>
                  <CloseIcon sx={{ fontSize: 14, color: "#8139D0" }} />
                  Remove
                </button>
              </div>
              <TextField
                placeholder="https://..."
                value={data.jobPostingUrl}
                onChange={(e) => {
                  onChange({ jobPostingUrl: e.target.value });
                  if (errors.jobPostingUrl) setErrors((prev) => ({ ...prev, jobPostingUrl: undefined }));
                }}
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors.jobPostingUrl}
                sx={INPUT_SX}
              />
              {errors.jobPostingUrl && <p className="rah-field-error">{errors.jobPostingUrl}</p>}
            </div>
          )}
        </div>

        {/* ── Experience level ── */}
        <div className="rah-form-card__field-group" style={{ gap: 4 }}>
          <p className="rah-form-card__field-label">Experience level</p>
          <ExperienceToggle value={data.experienceLevel} onChange={(v) => onChange({ experienceLevel: v })} />
        </div>

        {/* ── Ideal start date ── */}
        <div className="rah-form-card__field-group" style={{ gap: 4 }}>
          <p className="rah-form-card__field-label">Ideal start date</p>
          {/* @ts-ignore — JS component with defaultProps */}
          <CustomDatePicker
            name="startDate"
            label=""
            value={data.startDate}
            onChange={(date: Date | null) => {
              onChange({ startDate: date });
              if (errors.startDate && date) setErrors((prev) => ({ ...prev, startDate: undefined }));
            }}
            minDate={MIN_START_DATE}
            width="100%"
            helperText=""
            placeholder="Select start date"
            error={!!errors.startDate}
            disabled={false}
            PopperProps={{
              placement: "bottom-start",
              className: "date-picker-container",
              popperOptions: { strategy: "fixed" },
              modifiers: [
                { name: "offset", options: { offset: [0, 4] } },
                { name: "flip", enabled: false },
              ],
            }}
            sx={{
              height: 40,
              borderRadius: "4px",
              "& fieldset": { borderColor: "#DFDFDF" },
              "&:hover fieldset": { borderColor: "#DFDFDF" },
              "&.Mui-focused fieldset": { borderColor: "#8139D0" },
              "& .MuiInputBase-input": {
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                color: "#000000",
              },
            }}
          />
          <p className="rah-form-card__helper-text">
            {`Submit your request today and we'll have candidates ready for you as early as ${MIN_START_DATE.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.`}
          </p>
          {errors.startDate && <p className="rah-field-error">{errors.startDate}</p>}
        </div>
      </div>

      {/* Footer */}
      <div className="rah-form-card__footer">
        <button type="button" className="rah-form-card__back-btn" onClick={onBack}>
          <ChevronLeftIcon sx={{ fontSize: 22 }} />
          Back
        </button>
        <button type="button" className="rah-form-card__continue-btn" onClick={handleContinueClick}>
          {editMode ? "Save" : "Continue"}
          <ChevronRightIcon sx={{ fontSize: 22 }} />
        </button>
      </div>
    </div>
  );
}

export default StepBasics;
